import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Compact KPI tile (label + value + optional sub/icon) for dashboard headers.
// Uses design-system tokens only, so it follows the active theme automatically.
// Loading state is covered by SkeletonStatCard (same footprint).

export type StatCardAccent = 'primary' | 'success' | 'warning' | 'danger' | 'neutral';

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon?: IconDefinition;
  accent?: StatCardAccent;
  className?: string;
}

const accentIcon: Record<StatCardAccent, string> = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger: 'bg-danger-50 text-danger-600',
  neutral: 'bg-neutral-100 text-neutral-500',
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  sub,
  icon,
  accent = 'neutral',
  className = '',
}) => (
  <div className={`p-4 bg-surface rounded-xl border border-neutral-200 ${className}`.trim()}>
    <div className="flex items-center gap-3 mb-3">
      {icon && (
        <span className={`flex items-center justify-center w-10 h-10 rounded-lg ${accentIcon[accent]}`}>
          <FontAwesomeIcon icon={icon} className="text-base" />
        </span>
      )}
      <span className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500">
        {label}
      </span>
    </div>
    <div className="text-2xl font-semibold tabular-nums text-neutral-900">{value}</div>
    {sub && <div className="mt-1 text-xs text-neutral-500">{sub}</div>}
  </div>
);

StatCard.displayName = 'StatCard';

export default StatCard;
